// Each email identity becomes one block across the conversation, including intervening SMS and idle dividers.
export const normalizeEmailSubject = value => String(value || '').trim().replace(/^(?:(?:re|fwd|fw)\s*:\s*)+/i, '').trim().toLocaleLowerCase();
const ids = (value) => String(value || '').match(/<[^<>\s]+>/g) || [];
export function groupEmailTimeline(items = [], { groupBySubject = true } = {}) {
    const parent = new Map();
    const find = (id) => { if (!parent.has(id)) parent.set(id, id); if (parent.get(id) !== id) parent.set(id, find(parent.get(id))); return parent.get(id); };
    const keys = (item) => {
        const scope = `${item.emailProviderId || ''}:`;
        return [item.emailThreadId ? `${scope}thread:${item.emailThreadId}` : '', ...ids(item.emailMessageId).map((id) => `${scope}${id}`),
            ...ids(item.emailReferences).map((id) => `${scope}${id}`), ...ids(item.emailInReplyTo).map((id) => `${scope}${id}`)].filter(Boolean);
    };
    items.filter((i) => i.channel === 'email').forEach((item) => {
        const all = keys(item); all.slice(1).forEach((id) => parent.set(find(id), find(all[0])));
    });
    const output = [];
    const threads = new Map();
    for (const item of items) {
        if (item.emailDeleted || item.emailOutsideFolders) continue;
        if (item.channel !== 'email' || item.systemLine || item.source === 'scheduled' || item.kind === 'divider') { output.push(item); continue; }
        const all = keys(item);
        const subject = normalizeEmailSubject(item.emailSubject);
        const key = groupBySubject ? (subject ? `subject:${subject}` : all.length ? find(all[0]) : `message:${item.id}`) : `message:${item.id}`;
        if (threads.has(key)) threads.get(key).messages.push(item);
        else {
            const block = { kind: 'email-thread', id: `email-thread:${item.id}`, threadKey: key, messages: [item], at: item.at };
            threads.set(key, block); output.push(block);
        }
    }
    return output;
}
// Only fold a trailing, explicitly quoted run. Unprefixed replies, pasted prose and ambiguous Outlook blocks stay visible.
export function splitQuotedHistory(text) {
    const lines = String(text || '').split('\n');
    let start = lines.length;
    while (start > 0 && (/^\s*>/.test(lines[start - 1]) || !lines[start - 1].trim())) start--;
    if (!lines.slice(start).some((v) => /^\s*>/.test(v))) return { content: String(text || ''), quoted: '' };
    if (start > 0 && /^On .+\bwrote:\s*$/i.test(lines[start - 1])) start--;
    // Outlook header blocks are folded only when their body is explicitly quoted too.
    if (start >= 4 && /^From:\s*\S/i.test(lines[start - 4]) && /^Sent:\s*\S/i.test(lines[start - 3])
        && /^To:\s*\S/i.test(lines[start - 2]) && /^Subject:\s*\S/i.test(lines[start - 1])) start -= 4;
    if (start === 0) return { content: String(text || ''), quoted: '' }; // a message containing only a quote is still the new message
    return { content: lines.slice(0, start).join('\n').trimEnd(), quoted: lines.slice(start).join('\n') };
}

// Only pass rows read through the signed-in client's intake_sessions RLS query.
// Thread identity never comes from a subject or contact address. Joining is a
// display operation: ownership, stored sessions and mailbox action targets stay intact.
export function visibleEmailThreadSession(session, visibleSessions = []) {
    if (session?.channel !== 'email' || !session.org_id) return session;
    const mailbox = row => row.email_provider_id || [...new Set((row.raw_message_log || []).map(e => e.email_provider_id).filter(Boolean))].filter(Boolean).reduce((one, id, index, all) => all.length === 1 ? id : '', '');
    const provider = mailbox(session);
    if (!provider) return session;
    const identities = row => new Set([
        /^(graph|gmail|sync):/.test(row.external_thread_id || '') ? `thread:${row.external_thread_id.replace(/^(graph|gmail|sync):/, '')}` : '',
        ...(row.raw_message_log || []).flatMap(e => [
            e.thread_id || e.conversation_id ? `thread:${e.thread_id || e.conversation_id}` : '',
            ...ids(e.internet_message_id || e.message_id), ...ids(e.in_reply_to), ...ids(e.references),
        ]),
    ].filter(Boolean));
    const candidates = visibleSessions.filter(row => row.id !== session.id && row.channel === 'email' && row.org_id === session.org_id && mailbox(row) === provider);
    const chosen = [session];
    const keys = identities(session);
    let added = true;
    while (added) {
        added = false;
        for (const row of candidates) {
            if (chosen.some(item => item.id === row.id)) continue;
            const nextKeys = identities(row);
            const contactEmail = (session.contact_email || '').trim().toLowerCase();
            const sameContact = Boolean(contactEmail) && (row.contact_email || '').trim().toLowerCase() === contactEmail;
            const sameSubject = normalizeEmailSubject(row.email_subject) && normalizeEmailSubject(row.email_subject) === normalizeEmailSubject(session.email_subject);
            if (![...nextKeys].some(key => keys.has(key)) && !(sameContact && sameSubject)) continue;
            chosen.push(row); nextKeys.forEach(key => keys.add(key)); added = true;
        }
    }
    const raw = chosen.flatMap(row => (row.raw_message_log || []).map(entry => ({ ...entry, source_session_id: row.id,
        email_provider_id: entry.email_provider_id || provider, subject: entry.subject || row.email_subject,
        thread_id: entry.thread_id || entry.conversation_id || (/^(graph|gmail|sync):/.test(row.external_thread_id || '') ? row.external_thread_id.replace(/^(graph|gmail|sync):/, '') : undefined),
    })));
    return { ...session, raw_message_log: raw,
        conversation_log: chosen.flatMap(row => (row.conversation_log || []).map(entry => ({ ...entry, source_session_id: row.id }))),
    };
}
